/**
 * Media Cache Service - Manages caching of media blobs in IndexedDB
 *
 * This service provides a simple interface to store and retrieve media blobs
 * using their MXC URL as the key. It helps avoid re-downloading attachments
 * that have already been viewed.
 */

import logger from '../utils/logger';

const DB_NAME = 'ti-m-media-cache';
const DB_VERSION = 2; // Incremented to add roomId index
const STORE_NAME = 'media_blobs';

/**
 * F-06 boot safety net flag check. The matrixForceClearStores flag is set by
 * the logout path (LoginContext) when an IndexedDB deletion is blocked, and by
 * the crypto-init recovery path. When set at boot we wipe this app-owned DB
 * BEFORE re-opening it, so residue can never survive a blocked logout-delete.
 * The flag is intentionally LEFT in place here for matrixService.createClient()
 * to consume (it also drives crypto-store clearing). A healthy reload never has
 * the flag set, so the user's media cache is preserved across normal reloads.
 */
function shouldForceClearAppStores() {
  try {
    return localStorage.getItem('matrixForceClearStores') === 'true';
  } catch {
    return false;
  }
}

function isIndexedDBAvailable() {
  return typeof window !== 'undefined' &&
    !!window.indexedDB &&
    typeof window.indexedDB.open === 'function';
}

class MediaCacheService {
  constructor() {
    this.dbPromise = null;
    this.init();
  }

  /**
   * Initialize the IndexedDB database and object store.
   */
  init() {
    if (!isIndexedDBAvailable()) {
      logger.warn('IndexedDB is not supported in this browser. Media caching will be disabled.');
      return;
    }
    this.dbPromise = this._openWithBootCleanup();
  }

  /**
   * (Re)open the database lazily. close()/deleteDatabase() set dbPromise to null
   * (e.g. during logout); the next cache operation re-opens a fresh empty DB so
   * a logout+login WITHOUT a page reload keeps caching working — while the
   * post-logout window (before any op) still observes the DB as deleted.
   * @private
   */
  _ensureInitialized() {
    if (!this.dbPromise && isIndexedDBAvailable()) {
      this.init();
    }
  }

  /**
   * F-06: when the force-clear flag is set, delete any residual DB BEFORE
   * opening (the delete runs before any handle exists, so it is never blocked).
   * @private
   */
  async _openWithBootCleanup() {
    if (shouldForceClearAppStores()) {
      logger.info(`[MediaCache] Force-clear flag set — deleting residual ${DB_NAME} before re-open`);
      try {
        await this._rawDeleteDatabase();
      } catch (error) {
        logger.warn('[MediaCache] Boot cleanup delete failed (continuing to open):', error);
      }
    }
    return this._open();
  }

  /**
   * @private
   * @returns {Promise<IDBDatabase>}
   */
  _open() {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = (event) => {
        logger.error('IndexedDB error:', event.target.error);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        resolve(event.target.result);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME);
          // Add an index on roomId to efficiently find all media for a room.
          store.createIndex('roomId', 'roomId', { unique: false });
        } else if (event.oldVersion < 2) {
          const transaction = request.transaction;
          transaction.objectStore(STORE_NAME).createIndex('roomId', 'roomId', { unique: false });
        }
      };
    });
  }

  /**
   * Close the open IndexedDB handle so a subsequent deleteDatabase() is not
   * blocked (F-06). Safe to call when no handle is open. dbPromise is nulled
   * first so a concurrent op cannot grab the closing connection.
   * @returns {Promise<void>}
   */
  async close() {
    const pending = this.dbPromise;
    if (!pending) return;
    this.dbPromise = null;
    try {
      const db = await pending;
      db?.close?.();
      logger.debug('[MediaCache] Closed media-cache DB handle');
    } catch (error) {
      // The open had failed — there is nothing to close.
      logger.debug('[MediaCache] close: open had failed, nothing to close:', error?.message);
    }
  }

  /**
   * Close the handle and delete the media-cache database (F-06). Mirrors
   * indexedDBService.deleteDatabase(): resolves { blocked: true } when another
   * connection (e.g. a second tab) blocks the delete, so the logout path can
   * fall back to the next-load wipe.
   * @returns {Promise<{success: boolean, blocked?: boolean}>}
   */
  async deleteDatabase() {
    await this.close();
    return this._rawDeleteDatabase();
  }

  /**
   * Delete the database by constant name without touching dbPromise. Used both
   * by deleteDatabase() (after close()) and by the boot-cleanup path (before any
   * handle exists).
   * @private
   * @returns {Promise<{success: boolean, blocked?: boolean}>}
   */
  _rawDeleteDatabase() {
    return new Promise((resolve) => {
      if (typeof window === 'undefined' || !window.indexedDB ||
          typeof window.indexedDB.deleteDatabase !== 'function') {
        resolve({ success: true });
        return;
      }
      try {
        const req = window.indexedDB.deleteDatabase(DB_NAME);
        req.onsuccess = () => {
          logger.debug(`[MediaCache] Deleted database ${DB_NAME}`);
          resolve({ success: true });
        };
        req.onerror = (event) => {
          logger.warn(`[MediaCache] Error deleting database ${DB_NAME}:`, event?.target?.error);
          resolve({ success: false });
        };
        req.onblocked = () => {
          logger.warn(`[MediaCache] Delete of database ${DB_NAME} blocked — close all connections first`);
          resolve({ success: false, blocked: true });
        };
      } catch (error) {
        logger.warn(`[MediaCache] Exception deleting database ${DB_NAME}:`, error);
        resolve({ success: false });
      }
    });
  }

  /**
   * Get a media blob from the cache.
   * @param {string} mxcUrl - The MXC URL of the media.
   * @returns {Promise<Blob|null>} The cached blob, or null if not found.
   */
  async getMedia(mxcUrl) {
    this._ensureInitialized();
    if (!this.dbPromise) return null;
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(mxcUrl);

      request.onsuccess = () => {
        // The store now holds an object { blob, roomId }. Return just the blob.
        resolve(request.result?.blob || null);
      };
      request.onerror = (event) => reject(event.target.error);
    });
  }

  /**
   * Store a media blob in the cache.
   * @param {string} mxcUrl - The MXC URL of the media.
   * @param {Blob} blob - The media blob to store.
   * @param {string} [roomId] - The optional room ID to associate with the media.
   * @returns {Promise<void>}
   */
  async setMedia(mxcUrl, blob, roomId) {
    this._ensureInitialized();
    if (!this.dbPromise) return;
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      // Store an object containing the blob and the roomId.
      const dataToStore = { blob, roomId };
      const request = store.put(dataToStore, mxcUrl);

      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(event.target.error);
    });
  }

  /**
   * Remove a single media item from the cache.
   * @param {string} mxcUrl - The MXC URL of the media to remove.
   * @returns {Promise<void>}
   */
  async removeMedia(mxcUrl) {
    this._ensureInitialized();
    if (!this.dbPromise) return;
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(mxcUrl);

      request.onsuccess = () => {
        logger.debug(`[MediaCache] Removed media from cache: ${mxcUrl}`);
        resolve();
      };
      request.onerror = (event) => reject(event.target.error);
    });
  }

  /**
   * Remove all cached media associated with a specific room.
   * @param {string} roomId - The ID of the room to clear from the cache.
   * @returns {Promise<void>}
   */
  async removeMediaForRoom(roomId) {
    this._ensureInitialized();
    if (!this.dbPromise) return;
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('roomId');
      const request = index.openCursor(IDBKeyRange.only(roomId));

      let deleteCount = 0;
      let failCount = 0;

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const deleteRequest = store.delete(cursor.primaryKey);

          deleteRequest.onsuccess = () => {
            deleteCount++;
            cursor.continue();
          };

          deleteRequest.onerror = (e) => {
            failCount++;
            logger.error(`Failed to delete media ${cursor.primaryKey}:`, e.target.error);
            cursor.continue(); // Continue despite error
          };
        } else {
          // All cursor iterations complete
          if (failCount > 0) {
            logger.warn(`[MediaCache] Removed ${deleteCount} media items for room: ${roomId}, ${failCount} failed`);
          } else {
            logger.debug(`[MediaCache] Removed ${deleteCount} media items for room: ${roomId}`);
          }
          resolve({ deleted: deleteCount, failed: failCount });
        }
      };
      request.onerror = (event) => reject(event.target.error);

      // Optional: Handle transaction errors
      transaction.onerror = (event) => {
        logger.error('Transaction error during media removal:', event.target.error);
        reject(event.target.error);
      };
    });
  }
}

// Create a singleton instance
const mediaCacheService = new MediaCacheService();
export default mediaCacheService;
